// src/services/NeedService.ts
// ✅ UPDATED: Validation now checks 5-question fields instead of deprecated scope

import { needApi } from '@/lib/api/needApi';
import {
    Need,
    NeedListResponse,
    CreateNeedRequest,
    UpdateNeedRequest,
    UpdateNeedStatusRequest,
    NeedStatus
} from '@/lib/models/need';

export class NeedService {

    // ==================== NEED OPERATIONS ====================

    static async createNeed(data: CreateNeedRequest): Promise<Need> {
        return await needApi.createNeed(data);
    }

    static async getMyActiveNeeds(page: number = 1, limit: number = 20): Promise<NeedListResponse> {
        return await needApi.getMyActiveNeeds(page, limit);
    }

    static async getNeedById(needId: string): Promise<Need> {
        return await needApi.getNeedById(needId);
    }

    static async updateNeed(
        needId: string,
        data: UpdateNeedRequest
    ): Promise<Need> {
        return await needApi.updateNeed(needId, data);
    }

    static async updateNeedStatus(
        needId: string,
        status: NeedStatus
    ): Promise<Need> {
        return await needApi.updateNeedStatus(needId, { status });
    }

    static async deleteNeed(needId: string): Promise<void> {
        await needApi.deleteNeed(needId);
    }

    // ==================== ADMIN OPERATIONS ====================

    static async listAllNeeds(page: number = 1, limit: number = 20): Promise<NeedListResponse> {
        return await needApi.listAllNeeds(page, limit);
    }

    // ==================== HELPER FUNCTIONS ====================

    /**
     * Check if need can be edited
     */
    static canEditNeed(need: Need): boolean {
        return need.status === 'draft' || need.status === 'active';
    }

    /**
     * Check if need can be closed
     */
    static canCloseNeed(need: Need): boolean {
        return need.status !== 'filled' && need.status !== 'closed';
    }

    /**
     * Check if need can be deleted
     */
    static canDeleteNeed(need: Need): boolean {
        // Drafts and active postings can be removed; filled/closed are kept.
        return need.status === 'draft' || need.status === 'active';
    }

    /**
     * Check if need can be duplicated
     */
    static canDuplicateNeed(need: Need): boolean {
        // Can duplicate any need
        return true;
    }

    /**
     * Sort needs by status priority
     */
    static sortNeedsByPriority(needs: Need[]): Need[] {
        // #320 — this listed a stale 'interviewing' status and omitted the two real
        // ones added by the needs lifecycle work (#62/#281): paused and expired.
        // A paused or expired need therefore sorted on `undefined`, making every
        // comparison against it NaN and the order arbitrary.
        //
        // Expired first: the deadline has passed and it is waiting on the employer,
        // which is the one state that needs action rather than just attention.
        const statusPriority: Record<NeedStatus, number> = {
            expired: 1,
            active: 2,
            paused: 3,
            draft: 4,
            filled: 5,
            closed: 6,
        };

        return [...needs].sort((a, b) => {
            const priorityDiff = statusPriority[a.status] - statusPriority[b.status];
            if (priorityDiff !== 0) return priorityDiff;

            // If same status, sort by created date (newest first)
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
    }

    /**
     * Filter needs by status
     */
    static filterNeedsByStatus(needs: Need[], statuses: NeedStatus[]): Need[] {
        return needs.filter(need => statuses.includes(need.status));
    }

    /**
     * Get needs grouped by status
     */
    static groupNeedsByStatus(needs: Need[]): Record<NeedStatus, Need[]> {
        // Same omission, but here it CRASHED rather than mis-sorted: the loop below
        // does grouped[need.status].push(...), so a paused or expired need hit
        // `undefined.push` and threw.
        const grouped: Record<NeedStatus, Need[]> = {
            draft: [],
            active: [],
            paused: [],
            filled: [],
            closed: [],
            expired: [],
        };

        needs.forEach(need => {
            grouped[need.status].push(need);
        });

        return grouped;
    }

    /**
     * Format start date for display
     */
    static formatStartDate(startDate: string): string {
        const date = new Date(startDate);
        return date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * Validate create need request
     * ✅ UPDATED: Now validates 5-question fields instead of deprecated scope
     */
    static validateCreateNeedRequest(request: CreateNeedRequest): {
        valid: boolean;
        errors: string[];
    } {
        const errors: string[] = [];

        // Basic fields
        if (!request.title || request.title.trim() === '') {
            errors.push('Titel krävs');
        }

        if (!request.role || request.role.trim() === '') {
            errors.push('Roll krävs');
        }

        if (!request.competencies || request.competencies.length === 0) {
            errors.push('Minst en kompetens krävs');
        }

        // ==================== 5-QUESTION VALIDATION ====================

        // Question 1: Hours per week
        if (request.min_hours_per_week === undefined || request.min_hours_per_week === null) {
            errors.push('Minimitimmar krävs');
        } else if (request.min_hours_per_week < 1 || request.min_hours_per_week > 40) {
            errors.push('Minimitimmar måste vara mellan 1 och 40');
        }

        if (request.max_hours_per_week === undefined || request.max_hours_per_week === null) {
            errors.push('Maxtimmar krävs');
        } else if (request.max_hours_per_week < 1 || request.max_hours_per_week > 40) {
            errors.push('Maxtimmar måste vara mellan 1 och 40');
        }

        if (request.min_hours_per_week && request.max_hours_per_week) {
            if (request.min_hours_per_week > request.max_hours_per_week) {
                errors.push('Minimitimmar kan inte vara högre än maxtimmar');
            }
        }

        // Question 2: Start date
        if (!request.start_date) {
            errors.push('Startdatum krävs');
        } else {
            const date = new Date(request.start_date);
            if (isNaN(date.getTime())) {
                errors.push('Ogiltigt datumformat');
            }
        }

        // Question 3: City
        if (!request.city || request.city.trim() === '') {
            errors.push('Stad krävs');
        }

        // Question 4: Work location type
        if (request.work_location_on_site === undefined || request.work_location_remote === undefined) {
            errors.push('Arbetsplatstyp krävs');
        } else if (!request.work_location_on_site && !request.work_location_remote) {
            errors.push('Minst en arbetsplatstyp måste väljas (på plats eller distans)');
        }

        // Question 5: Supervision (boolean, no validation needed but check it's defined)
        if (request.offers_supervision === undefined || request.offers_supervision === null) {
            errors.push('Handledning måste anges');
        }

        // ================================================================

        return { valid: errors.length === 0, errors };
    }
}